// Core engine types. Every rule implemented here traces to HANDOFF.md / PLAN.md §4.
// Dates are ISO calendar days ("YYYY-MM-DD"), compared as strings.

export type ISODate = string;

export const BREAKDOWNS = ["wbs", "pbs", "obs", "dbs", "cbs", "sbs"] as const;
export type Breakdown = (typeof BREAKDOWNS)[number];
export type Codes = Partial<Record<Breakdown, string>>;

export interface Period {
  number: number;
  start: ISODate;
  cutoff: ISODate;
  stdHours: number;
  /** Rule 18: a checkbox the user ticks on a cutoff. Monthly charts use flagged cutoffs. */
  monthEnd: boolean;
}

export interface ChainStep {
  milestone: string;
  /** Cumulative fraction 0..1 (rule 3). */
  cum: number;
  /** Procurement "manufacturing %" (the [20%] in "40%[20%]"). Stored only; use is open Q2. */
  mfgCum?: number;
}

export interface Chain {
  code: string;
  title?: string;
  category?: string;
  steps: ChainStep[];
}

export interface DeliverableStep {
  milestone: string;
  cum: number;
  planned?: ISODate | null;
  forecast?: ISODate | null;
  /** Rule 4: "Earned" date (formerly "Actual"). */
  earned?: ISODate | null;
  /** Rule 4: each milestone carries its own PBS/WBS/OBS; blank falls back to the deliverable's. */
  codes?: Codes;
  /** Rule 4: free-text "Planning System Support Import" (P6 activity id). */
  planningRef?: string;
}

/** Rule 17: a deliverable weight change order, effective from its approval period. */
export interface WeightChange {
  periodNumber: number;
  weight: number;
  ref?: string;
}

export type Unit = "h" | "$";

/**
 * Level of effort (Cesar, Oct 15): a deliverable with no milestones that earns, in each elapsed period, the budget
 * planned for that period. Its budget is spread evenly per period between its dates, the same way as an indirect
 * (rule 20), but it is discipline scope and counts in BAC, PV, EV and the budget. Stored with this chain code.
 */
export const LOE_CHAIN = "LOE";
export interface LoeSpan {
  start: ISODate;
  end: ISODate;
}

export interface Deliverable {
  id: string;
  code: string;
  title?: string;
  weight: number;
  /** Open Q2: hours vs $. Different units are never summed together. */
  unit?: Unit;
  codes: Codes;
  chainCode: string;
  steps: DeliverableStep[];
  weightChanges?: WeightChange[];
}

/** Rule 12: actual (expended) hours, discipline level with breakdown allocation, per period. */
export interface ActualEntry {
  periodNumber: number;
  hours: number;
  codes: Codes;
  indirect?: boolean;
}

/** Rule 20: indirect / non-progressable hours. */
export interface IndirectItem {
  id: string;
  title?: string;
  budget: number;
  start: ISODate;
  end: ISODate;
  codes: Codes;
}

export type Series = "planned" | "earned" | "forecast";
export const SERIES: Series[] = ["planned", "earned", "forecast"];

export interface Booking {
  periodIdx: number;
  stepIdx: number; // 0-based index into deliverable.steps
  value: number;
}

/** Frozen state from a period close (rule 16). Bookings for periods <= lastClosedIdx are authoritative. */
export interface FrozenDeliverable {
  bookings: Record<Series, Booking[]>;
  /** Highest step index (1-based count) reached per series at close. */
  reached: Record<Series, number>;
}

export interface Frozen {
  lastClosedIdx: number;
  deliverables: Record<string, FrozenDeliverable>;
}

export type IndirectEarnedMode = "actual" | "planned";

export interface Settings {
  /** Rule 13 toggle. Default off. */
  includeIndirects: boolean;
  /** Open Q4: earned value of indirects when included. */
  indirectEarned: IndirectEarnedMode;
  /** Rule 15: cost = hours x project labor rate. */
  laborRate: number;
  /** Rule 22 toggle. */
  autoForecast: boolean;
  /** Open Q3. */
  autoForecastSpacing: "equal" | "proportional";
}

export const DEFAULT_SETTINGS: Settings = {
  includeIndirects: false,
  indirectEarned: "actual",
  laborRate: 0,
  autoForecast: false,
  autoForecastSpacing: "equal",
};
